import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ColorUtils, platformCmdOrCtrlKey } from '@app/shared/utils';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import {
  BreakDeletionComponent,
  BreakDetailsAndOcc,
  BreakEditionComponent,
  BreakListOccurrence,
  BreakPopoverComponent,
} from '@radioking/break';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppState } from '@app/core/states/app.state';
import { AuthState } from '@app/core/states/auth.state';
import { MOBILE_DIALOG_CONFIG } from '@app/shared/constants';
import { BreakListBreak, BreakListHoursOccurrences } from '../../models/break.model';
import { ResetBreak, SelectBreaks } from '../../states/break.actions';
import { BreakState } from '../../states/break.state';

@Component({
  selector: 'rk-break-list',
  templateUrl: './break-list.component.html',
  styleUrls: ['./break-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreakListComponent implements OnInit {
  @Select(BreakState.hoursOccurrences)
  hoursOccurrences$: Observable<BreakListHoursOccurrences[]>;

  @Select(BreakState.currentDay)
  currentDay$: Observable<moment.Moment>;

  @Select(AppState.onMobile)
  onMobile$: Observable<boolean>;

  @Select(AuthState.userLang)
  userLang$: Observable<string>;

  @Input()
  breaks: BreakListBreak[];

  @Output()
  scrolled = new EventEmitter<boolean>(false);

  mobile = false;
  // Used to detect scrolling direction
  scrollTop = 0;
  // Used to determine max scroll down level, to put back big header
  scrollDownLevel = 0;

  $filteredHours: WritableSignal<BreakListHoursOccurrences[]> = signal(null);
  $filteredBreaks: WritableSignal<BreakListBreak[]> = signal(null);

  popoverDialogRef: MatDialogRef<BreakPopoverComponent>;

  selectedEvents: BreakListOccurrence[] = [];
  selectedIds: number[] = [];
  lastSelectedEvent: BreakListOccurrence = null;

  subscription = new Subscription();

  constructor(
    private readonly dialog: MatDialog,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    combineLatest([this.hoursOccurrences$, this.currentDay$, this.onMobile$]).subscribe(
      ([hoursOccurrences, currentDay, onMobile]) => {
        this.mobile = onMobile;
        this.$filteredHours.set(hoursOccurrences);
        if (onMobile) {
          const hoursOccurence: BreakListHoursOccurrences[] = [];
          hoursOccurrences?.forEach(hourOccs => {
            const hourOccurence: BreakListHoursOccurrences = {
              startHour: hourOccs.startHour,
              hour24h: hourOccs.hour24h,
              occurrences: hourOccs.occurrences.filter(
                occ => occ && occ[0].startDate.isSame(currentDay, 'day'),
              ),
            };
            if (hourOccurence.occurrences.length) {
              hoursOccurence.push(hourOccurence);
            }
          });
          this.$filteredHours.set(hoursOccurence);
        }
      },
    );
  }

  getBreak(breakId: number) {
    return this.breaks.find(b => b.id === breakId);
  }

  getSlotStyle(breakObj: BreakListBreak) {
    return {
      'background-color': breakObj.color,
      color: ColorUtils.bestContrastTextColor(breakObj.color),
    };
  }

  getIconStyle(breakObj: BreakListBreak): string {
    return `/assets/icons/check${
      ColorUtils.bestContrastTextColor(breakObj.color) === '#fff' ? '' : '-dark'
    }.svg`;
  }

  handleBreakPopover(
    occurrence: BreakListOccurrence,
    breakObj: BreakListBreak,
    e: Event,
  ): void {
    this.resetSelection();
    this.resetCurrentBreak();
    this.dialog.closeAll();
    const dialogConfig = this.mobile
      ? {
          data: { occurrence, breakObj, el: e.target, mobile: true },
          closeOnNavigation: true,
          ...MOBILE_DIALOG_CONFIG,
        }
      : {
          data: { occurrence, breakObj, el: e.target },
          closeOnNavigation: true,
          hasBackdrop: false, // See closeOpenedDialogsOnOutsideClick()
          panelClass: 'cdk-overlay-pane-transparent-dialog',
        };

    const dialogRef = this.dialog.open(BreakPopoverComponent, dialogConfig);

    this.subscription.add(
      dialogRef.componentInstance.breakDeletion
        .pipe(takeUntil(dialogRef.afterClosed()))
        .subscribe(data => {
          this.breakDeletion(data);
        }),
    );

    this.subscription.add(
      dialogRef.componentInstance.breakEdition
        .pipe(takeUntil(dialogRef.afterClosed()))
        .subscribe(data => {
          this.breakEdition(data);
        }),
    );

    this.subscription.add(
      dialogRef.afterOpened().subscribe(() => {
        this.popoverDialogRef = dialogRef;
      }),
    );

    this.subscription.add(
      dialogRef.afterClosed().subscribe(() => {
        this.popoverDialogRef = null;
      }),
    );
  }

  onContextMenu(occurrence: BreakListOccurrence, breakObj: BreakListBreak, e: Event) {
    this.handleBreakPopover(occurrence, breakObj, e);

    e.preventDefault();
    e.stopPropagation();
  }

  breakDeletion(data: BreakDetailsAndOcc): void {
    this.dialog.closeAll();

    this.dialog.open(
      BreakDeletionComponent,
      this.mobile
        ? {
            data,
            closeOnNavigation: true,
            ...MOBILE_DIALOG_CONFIG,
          }
        : {
            data,
            closeOnNavigation: true,
          },
    );
  }

  breakEdition(data: BreakDetailsAndOcc): void {
    this.dialog.closeAll();

    this.dialog.open(
      BreakEditionComponent,
      this.mobile
        ? {
            data: { ...data, mobile: true },
            closeOnNavigation: true,
            ...MOBILE_DIALOG_CONFIG,
            panelClass: 'full-mobile-dialog',
          }
        : {
            data: data,
            closeOnNavigation: true,
          },
    );
  }

  handleEventSelect(
    occurrence: BreakListOccurrence,
    breakObj: BreakListBreak,
    e: MouseEvent,
  ): void {
    const elem: HTMLElement = e.target as HTMLElement;

    if (!platformCmdOrCtrlKey(e) && !e.shiftKey) {
      this.handleBreakPopover(occurrence, breakObj, e);
      return;
    }

    if (platformCmdOrCtrlKey(e)) {
      // Meta click, add/remove current event
      if (elem.classList.contains('selected')) {
        this.selectedEvents = this.selectedEvents.filter(
          ev => ev.occurrenceId !== occurrence.occurrenceId,
        );
        this.lastSelectedEvent = this.selectedEvents.length
          ? this.selectedEvents[this.selectedEvents.length - 1]
          : null;
      } else {
        this.selectedEvents = [...this.selectedEvents, occurrence];
        this.lastSelectedEvent = occurrence;
      }
    } else if (e.shiftKey) {
      // Shift click, select events in range
      if (!this.selectedEvents.length) {
        this.selectedEvents = [occurrence];
        this.lastSelectedEvent = occurrence;
      } else {
        this.selectedEvents = this.eventsInRange(
          this.lastSelectedEvent.startDate,
          occurrence.startDate,
        );
      }
    }
    this.selectedIds = this.selectedEvents.map(occ => occ.occurrenceId);
    this.selectBreaks();
  }

  eventsInRange(from: moment.Moment, to: moment.Moment): BreakListOccurrence[] {
    const allOccurrences = this.store.selectSnapshot(BreakState.hoursOccurrences);
    const earlier = from.isAfter(to) ? to : from;
    const future = from.isSameOrBefore(to) ? to : from;
    const events: BreakListOccurrence[] = allOccurrences
      .map(a => a.occurrences)
      .reduce((a, v) => a.concat(v), [])
      .reduce((a, v) => a.concat(v), [])
      .filter(occ => !!occ && occ.startDate.isBetween(earlier, future, undefined, '[]'));
    return events;
  }

  resetSelection() {
    this.selectedEvents = [];
    this.lastSelectedEvent = null;
    const events = document.querySelectorAll('.fc-selected');
    Array.from(events).forEach(ev => ev.classList.remove('fc-selected'));
    this.selectBreaks();
  }

  @Dispatch()
  selectBreaks = () => new SelectBreaks(this.selectedEvents.map(e => e.occurrenceId));

  @Dispatch()
  resetCurrentBreak(event: Event = null) {
    if (!event) {
      return new ResetBreak();
    }
    const eventTarget = event.target as HTMLElement;

    if (eventTarget.scrollTop > this.scrollTop && eventTarget.scrollTop > 90) {
      // scrolling down
      this.scrollDownLevel = eventTarget.scrollTop;
      this.scrolled.emit(true);
    } else if (
      eventTarget.scrollTop < this.scrollTop &&
      this.scrollDownLevel - eventTarget.scrollTop > 40
    ) {
      // scrolling up
      this.scrolled.emit(false);
    }
    this.scrollTop = eventTarget.scrollTop;

    return new ResetBreak();
  }
}
